import React, { useState } from 'react';
import { useLocale } from '../context/LocaleContext';
import styles from '../styles/videoModal.module.css';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string;
  title?: string;
}

const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, videoId, title }) => {
  const { t } = useLocale();
  const [isLoading, setIsLoading] = useState(true);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Extract video ID from YouTube URL if full URL is provided
  const getVideoId = (idOrUrl: string): string => {
    // If it's already just a video ID, return it
    if (!idOrUrl.includes('youtube.com') && !idOrUrl.includes('youtu.be')) {
      return idOrUrl;
    }
    
    // Extract video ID from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = idOrUrl.match(pattern);
      if (match) {
        return match[1];
      }
    }
    
    return idOrUrl; // Fallback to original if no pattern matches
  };

  const embedVideoId = getVideoId(videoId);
  const embedUrl = `https://www.youtube.com/embed/${embedVideoId}?rel=0&modestbranding=1&showinfo=0`;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {title || t('video.default_title')}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className={styles.videoContainer}>
          {isLoading && (
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
              <p>{t('video.loading')}</p>
            </div>
          )}
          
          <iframe
            src={embedUrl}
            title={title || t('video.iframe_title')}
            className={styles.videoIframe}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={handleIframeLoad}
          />
        </div>
        
      </div>
    </div>
  );
};

export default VideoModal;
